import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideIonicAngular } from '@ionic/angular/standalone';
import {
  OidcSecurityService,
  provideAuth,
  authInterceptor as oidcBearerInterceptor,
} from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { API_BASE_URL } from './core/api/core-api.client';
import { authConfig } from './core/auth/auth.config';
import { authInterceptor } from './core/auth/auth.interceptor';
import { expectedClientInterceptor } from './core/session/expected-client.interceptor';
import { applyTabBrand, HOST_BRANDING, SERVED_HOSTNAME } from './core/auth/product-brand';
import { SessionService } from './core/session/session.service';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideIonicAngular({ mode: 'md' }),
    /**
     * Order matters. oidcBearerInterceptor ATTACHES the bearer to the URLs listed in
     * authConfig.secureRoutes — `secureRoutes` alone does nothing, the interceptor has to be
     * registered, or every API call goes out unauthenticated and comes back 401.
     *
     * Ours runs after it and only handles expiry: renew once, replay, and let a surviving 401
     * surface as an error rather than a login redirect.
     *
     * expectedClientInterceptor runs last: it stamps every write with the client this screen was
     * built for, and turns the API's refusal into a "switch back and save / stay" question (card 253).
     */
    provideHttpClient(
      withInterceptors([oidcBearerInterceptor(), authInterceptor, expectedClientInterceptor]),
    ),
    provideAuth(authConfig),

    // The generated clients take their origin from this token — the single API origin, so no client
    // ever learns a service's hostname or which backend owns an endpoint.
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },

    /**
     * Boot order matters. checkAuth() must settle first — it is what processes the /callback code
     * exchange and restores an existing session — and only THEN can /api/session be called with a
     * bearer token. Loading the session here rather than in a component applies the client's palette
     * before first paint, so there is no flash of the default brand.
     *
     * A failure must not block boot: an anonymous visitor legitimately has no session, and the route
     * guard will send them to login.
     */
    // The tab's icon and title by host, started first so an anonymous visitor on a customer's domain
    // sees that customer's icon on the login page too. Deliberately NOT awaited: boot must not wait on
    // a branding call, and a slow answer only means the ComputAll icon shows a moment longer.
    provideAppInitializer(() => {
      const branding = inject(HOST_BRANDING);
      void applyTabBrand(inject(DOCUMENT), inject(SERVED_HOSTNAME), () => branding, environment.apiBaseUrl);
    }),

    provideAppInitializer(async () => {
      const oidc = inject(OidcSecurityService);
      const session = inject(SessionService);

      const { isAuthenticated } = await firstValueFrom(oidc.checkAuth());
      if (!isAuthenticated) {
        return;
      }

      try {
        await session.load();
      } catch {
        // Leave the app bootable; the guard handles re-authentication.
      }
    }),
  ],
};
